import React, { useState } from 'react'
import Banner from '@components/Banner'
import { Container } from '@components/Globals'
import { Row } from '@components/Common'
import { StyledBannerH1 } from '@components/Banner/style'
import practiceAreaBgImage from '@assets/images/Practice_Area_Fallback.png'
import {
  PracticeAreaCurrentTabPanel,
  PracticeAreaTabsRow,
} from './subcomponents'
import { useLocale } from '@hooks'
import {
  filterSampleEntries,
  nodeLocaleToEnglishLanguage,
  sortOrder,
} from '@utils'

const PracticeAreaContent = ({
  parentPath,
  practiceArea,
  professionals,
  news = [],
  events = [],
  insights = [],
  accolades = [],
}) => {
  const cmsHeroImage = practiceArea?.headerBackgroundImage?.file?.url
  const heroImage = {
    fluid: {
      src: cmsHeroImage || practiceAreaBgImage,
    },
  }
  const prosOff = practiceArea?.prosOff
  const newsOff = practiceArea?.newsOff
  const insightsOff = practiceArea?.insightsOff

  const [tab, setTab] = useState(0)
  const { locale } = useLocale()

  const practiceChairEntries = practiceArea?.practiceChairs
    ?.filter(
      ({ professional }) => professional?.[0]?.name && professional?.[0]?.active
    )
    ?.map(chair => ({ ...chair?.professional?.[0], title: chair?.chairTitle }))

  const practiceChairs = {
    title: practiceArea?.practiceChairTitle,
    professionals: practiceChairEntries,
  }

  const {
    title,
    content,
    descriptionHtml,
    featuredCallouts: callouts,
  } = practiceArea

  const filterEntries = entries =>
    filterSampleEntries(entries, true)?.filter(({ displayOnSites }) =>
      displayOnSites?.includes(nodeLocaleToEnglishLanguage[locale])
    )

  const filteredNews = filterEntries(news)
  const filteredInsights = filterEntries(insights)
  const filteredEvents = filterEntries(events)
  const filteredAccolades = filterEntries(accolades)
  const filteredRelatedPracticeAreas = filterEntries(
    practiceArea?.relatedPracticeAreas
  )

  const eventsWithPublicationDate = filteredEvents?.map(
    ({ publicationDate, eventStart, ...rest }) => ({
      ...rest,
      eventStart,
      publicationDate: publicationDate || eventStart,
    })
  )

  const accoladesWithPublicationDate = filteredAccolades?.map(
    ({ publicationDate, ...rest }) => ({
      ...rest,
      publicationDate: publicationDate,
      __typename: 'ContentfulAccolade',
    })
  )

  const sortedEventsAndNews = filteredNews
    ?.concat(eventsWithPublicationDate)
    ?.concat(accoladesWithPublicationDate)
    ?.sort(sortOrder('desc', 'publicationDate'))

  const filteredProfessionals = filterSampleEntries(
    professionals,
    true
  )?.filter(({ active }) => active)

  const sanitizedProfesionals = filteredProfessionals?.map(
    ({ offices, tertiaryOffice, ...rest }) => {
      return {
        ...rest,
        offices: tertiaryOffice
          ? [...offices?.slice(0, 2), tertiaryOffice]
          : offices,
      }
    }
  )

  const practiceArray = [
    practiceArea,
    insightsOff ? null : insights,
    prosOff ? null : sanitizedProfesionals,
    newsOff ? null : sortedEventsAndNews,
  ]

  return (
    <>
      <Banner
        heroImage={heroImage}
        height="xs"
        gradient="blue"
        breadcrumbs
        to={parentPath}
        crumbLabel={'Practice Areas'}
        padding
      >
        <StyledBannerH1>{title}</StyledBannerH1>
      </Banner>
      <Container style={{ maxWidth: '100%', width: '100%' }}>
        <Row>
          <PracticeAreaTabsRow
            tab={tab}
            setTab={setTab}
            practiceArray={practiceArray}
          />
          <PracticeAreaCurrentTabPanel
            practiceArray={practiceArray}
            tab={tab}
            setTab={setTab}
            content={content}
            descriptionHtml={descriptionHtml}
            insights={filteredInsights}
            news={sortedEventsAndNews}
            professionals={filteredProfessionals}
            practiceChairs={practiceChairs}
            relatedPracticeAreas={filteredRelatedPracticeAreas}
            callouts={callouts}
          />
        </Row>
      </Container>
    </>
  )
}

export default PracticeAreaContent
